import React from "react";
import { motion } from "framer-motion";
import { data } from "../../data";

const SectionHeader = ({ period, title, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.35, delay, ease: [0.25, 1, 0.5, 1] }}
    className="flex flex-col items-center text-center mb-4"
  >
    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-gray-400 mb-2">
      <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
      {period}
    </span>
    <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
      <span className="bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">{title}</span>
    </h2>
    <span className="mt-1.5 block h-px w-12 bg-gradient-to-r from-designColor/50 to-transparent rounded-full" />
  </motion.div>
);

/* Featured (first) job — large hero-style card */
const FeaturedJob = ({ year, title, subtitle, image, details, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] hover:border-designColor/30 transition-all duration-300"
  >
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_70%_0%,rgba(255,1,79,0.06),transparent_50%)] pointer-events-none" />
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-designColor/80 via-pink-500/50 to-transparent pointer-events-none" />
    <div className="relative p-5 sml:p-8">
      <div className="flex flex-col sml:flex-row sml:items-start sml:justify-between gap-3 sml:gap-6">
        <div className="flex items-center gap-4">
          {image && (
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center p-2">
              <img src={image} alt="" className="w-full h-full object-contain" />
            </div>
          )}
          <div>
            <span className="inline-block rounded-full bg-designColor/20 text-designColor text-xs font-semibold px-3 py-1 mb-2">
              Current role
            </span>
            <h3 className="text-xl sml:text-3xl font-bold text-white group-hover:text-designColor transition-colors duration-200">
              {title}
            </h3>
            <p className="text-gray-400 mt-1">{subtitle}</p>
          </div>
        </div>
        <span className="flex-shrink-0 text-sm font-semibold text-designColor">{year}</span>
      </div>
      <ul className="mt-6 space-y-2 list-none pl-0">
        {Array.isArray(details) &&
          details.map((val, j) => (
            <li key={j} className="flex gap-3 text-gray-400 text-sm sm:text-base leading-relaxed">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-designColor mt-2" />
              <span>{val}</span>
            </li>
          ))}
      </ul>
    </div>
  </motion.article>
);

/* Past roles — compact grid cards */
const PastJobCard = ({ year, title, subtitle, image, details, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.4, delay: i * 0.06, ease: [0.25, 1, 0.5, 1] }}
    className="group h-full rounded-xl border border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05] transition-all duration-300 p-5 flex flex-col"
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      {image && (
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center p-1.5">
          <img src={image} alt="" className="w-full h-full object-contain" />
        </div>
      )}
      <span className="text-xs font-semibold text-designColor whitespace-nowrap">{year}</span>
    </div>
    <h3 className="text-lg font-bold text-white group-hover:text-designColor transition-colors duration-200">
      {title}
    </h3>
    <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
    <ul className="mt-4 space-y-2 list-none pl-0 flex-1">
      {Array.isArray(details) &&
        details.map((val, j) => (
          <li key={j} className="flex gap-2 text-gray-400 text-xs sml:text-sm leading-relaxed">
            <span className="flex-shrink-0 w-1 h-1 rounded-full bg-designColor/60 mt-1.5" />
            <span>{val}</span>
          </li>
        ))}
    </ul>
  </motion.article>
);

const EducationEntry = ({ year, degree, name, address, i }) => (
  <motion.div
    initial={{ opacity: 0, x: 8 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-20px" }}
    transition={{ duration: 0.3, delay: i * 0.05, ease: [0.25, 1, 0.5, 1] }}
    className="group py-3 border-b border-white/10 last:border-0 last:pb-0"
  >
    <span className="text-xs font-semibold text-designColor tracking-wide">{year}</span>
    <h3 className="text-sm font-semibold text-white mt-1 group-hover:text-designColor transition-colors duration-200 leading-tight">
      {degree}
    </h3>
    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{name}</p>
    {address && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{address}</p>}
  </motion.div>
);

const Education = () => {
  const jobs = data?.job ?? [];
  const [featured, ...pastJobs] = jobs;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-10"
    >
      <div className="min-w-0 space-y-8">
        <SectionHeader period="2021 – Present" title="Job experience" delay={0} />

        {featured && (
          <FeaturedJob
            year={featured.year}
            title={featured.position}
            subtitle={featured.company}
            image={featured.image}
            details={featured.responsibilities}
            i={0}
          />
        )}

        {pastJobs.length > 0 && (
          <>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-widest">Previous roles</h3>
            <div className="grid grid-cols-1 sml:grid-cols-2 gap-4">
              {pastJobs.map((value, i) => (
                <PastJobCard
                  key={value?.position + value?.company}
                  year={value?.year}
                  title={value?.position}
                  subtitle={value?.company}
                  image={value?.image}
                  details={value?.responsibilities}
                  i={i}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="lg:pt-12">
        <div className="sticky top-24 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5">
          <div className="mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
              <span className="h-1 w-1 rounded-full bg-designColor" />
              2012 – 2019
            </span>
            <h2 className="text-base sm:text-lg font-bold text-white mt-2 tracking-tight">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Education</span>
            </h2>
            <span className="mt-1.5 block h-px w-8 bg-gradient-to-r from-designColor/50 to-transparent rounded-full" />
          </div>
          {data?.education?.map((value, i) => (
            <EducationEntry
              key={value?.degree + value?.name}
              year={value?.year}
              degree={value?.degree}
              name={value?.name}
              address={value?.address}
              i={i}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default Education;
